/**
 * Rynoka track-order — /pages/track-order
 * Renders the result panel from the Claude Design mock
 * ("Rynoka Track Order Page.dc.html"): status timeline, items, address.
 *
 * Data sources, in priority order:
 *   1. Sample payload  — only when "Show sample result" is on in the editor.
 *   2. Custom tracking API — when the section's tracking source is `api`.
 *   3. Multi-carrier routing — `carrier` blocks give each courier a URL
 *      template and an AWB pattern; routeCarrier() picks one. See the ladder
 *      documented on routeCarrier itself.
 *   4. Legacy single-Shiprocket hand-off — what runs when no carrier blocks
 *      exist, byte-identical to the behaviour before multi-carrier support.
 *
 * On (3): the on-page timeline is real only for AWBs present in the index that
 * scripts/track-sync.mjs mirrors out of Shopify's own fulfillments. Items and
 * address are NEVER in that index — it is public in page source — so those
 * cards only ever render from the sample payload or a custom API.
 */
(function () {
  'use strict';

  if (window.__tlTrackOrderReady) return;
  window.__tlTrackOrderReady = true;

  var ENTER = 'is-in';

  /* ---------------------------------------------------------------- utils */

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** ₹1,298 — Indian grouping, no paise (store-wide rule). */
  function fmtINR(n) {
    var num = Number(n);
    if (!isFinite(num)) return '';
    return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function cleanAwb(v) {
    return String(v || '')
      .replace(/\s+/g, '')
      .trim()
      .toUpperCase();
  }

  function readJson(section, selector) {
    var el = section.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function attr(section, name, fallback) {
    var v = section.getAttribute(name);
    return v == null || v === '' ? fallback : v;
  }

  function config(section) {
    return {
      provider: attr(section, 'data-tl-provider', 'shiprocket_branded'),
      srBase: attr(section, 'data-tl-sr-base', 'https://shiprocket.co').replace(/\/+$/, ''),
      srPath: attr(section, 'data-tl-sr-path', '/tracking'),
      srStyle: attr(section, 'data-tl-sr-style', 'query'),
      srParam: attr(section, 'data-tl-sr-param', 'awb'),
      srEmbed: attr(section, 'data-tl-sr-embed', 'new_tab'),
      api: attr(section, 'data-tl-api', ''),
      msgEmpty: attr(section, 'data-tl-msg-empty', 'Please enter your AWB number to track.'),
      msgNotFound: attr(
        section,
        'data-tl-msg-notfound',
        "We couldn't find that AWB number. Double-check the digits and try again."
      ),
      msgError: attr(
        section,
        'data-tl-msg-error',
        "We couldn't reach the courier just now. Please try again in a moment."
      ),
      handoffTitle: attr(section, 'data-tl-handoff-title', 'Live tracking opened on Shiprocket'),
      handoffBlocked: attr(
        section,
        'data-tl-handoff-blocked',
        'Tap the button below to open live tracking'
      ),
      handoffCta: attr(section, 'data-tl-handoff-cta', 'View live tracking on Shiprocket'),
      handoffNote: attr(section, 'data-tl-handoff-note', ''),
      helpCta: attr(section, 'data-tl-help-cta', 'Need Help With This Order?'),
      contactUrl: attr(section, 'data-tl-contact-url', '/pages/contact'),
      itemsHeading: attr(section, 'data-tl-items-heading', 'Items in This Order'),
      addressHeading: attr(section, 'data-tl-address-heading', 'Delivering To'),
      totalLabel: attr(section, 'data-tl-total-label', 'Total Paid'),
      copied: attr(section, 'data-tl-copied', 'Copied!'),
      /* Multi-carrier additions — all default to today's behaviour when absent. */
      chooserTitle: attr(section, 'data-tl-chooser-title', 'Which courier has your parcel?'),
      chooserNote: attr(
        section,
        'data-tl-chooser-note',
        "We couldn't tell from this number alone. Pick the courier named in your shipping SMS or email."
      ),
      carrierLabel: attr(section, 'data-tl-carrier-label', 'Courier'),
      liveCta: attr(section, 'data-tl-live-cta', 'View live tracking on [carrier]'),
      lastUpdate: attr(section, 'data-tl-last-update', 'Last update')
    };
  }

  /* ------------------------------------------------------- carrier registry */

  /**
   * Carrier blocks, in schema order. Each: { code, name, url_template,
   * awb_pattern, accent, is_fallback }. Empty when the section has no carrier
   * blocks — every caller then falls back to the legacy Shiprocket settings,
   * so an un-migrated theme behaves exactly as it did before.
   */
  function carriers(section) {
    var list = readJson(section, '[data-tl-carriers]');
    return Array.isArray(list) ? list : [];
  }

  /** The first block ticked "handles anything" — Shiprocket, in practice. */
  function fallbackCarrier(list) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].is_fallback) return list[i];
    }
    return null;
  }

  function carrierByCode(list, code) {
    if (!code) return null;
    var want = String(code).toLowerCase();
    for (var i = 0; i < list.length; i++) {
      if (list[i] && String(list[i].code).toLowerCase() === want) return list[i];
    }
    return null;
  }

  /** Copy token: [carrier] is the house style; {carrier} tolerated as an alias. */
  function withCarrierName(tpl, name) {
    return String(tpl == null ? '' : tpl)
      .replace(/\[carrier\]/gi, name)
      .replace(/\{carrier\}/gi, name);
  }

  /** `https://x/track/{awb}` -> a real URL. Also accepts a bare base URL. */
  function fillTemplate(tpl, awb) {
    var t = String(tpl || '').trim();
    if (!t) return '';
    if (t.indexOf('{awb}') === -1) {
      return t.replace(/\/+$/, '') + '/' + encodeURIComponent(awb);
    }
    return t.replace(/\{awb\}/g, encodeURIComponent(awb));
  }

  /**
   * Carriers whose awb_pattern matches. Fallback carriers never pattern-match —
   * they are the last resort, not a candidate, so they can't create a phantom
   * ambiguity with a carrier that genuinely recognises the number.
   */
  function matchByPattern(list, awb) {
    var hits = [];
    list.forEach(function (c) {
      if (!c || c.is_fallback || !c.awb_pattern) return;
      var re;
      try {
        re = new RegExp(c.awb_pattern, 'i');
      } catch (e) {
        return; // a typo'd pattern in the editor must not break the lookup
      }
      if (re.test(awb)) hits.push(c);
    });
    return hits;
  }

  /* ----------------------------------------------------------- AWB index */

  /**
   * AWB -> shipment facts, mirrored from Shopify's own fulfillments by
   * scripts/track-sync.mjs. Keys are hashes (see hashAwb), never the AWB
   * itself: this blob is public in page source, and a plaintext AWB list would
   * let anyone paste other people's numbers into the courier trackers.
   *
   * Shape: { "<hash>": "<carrierCode>|<statusKey>|<YYYY-MM-DD>" }
   */
  function awbIndex(section) {
    var merged = null;
    var nodes = section.querySelectorAll('[data-tl-awb-index]');
    for (var i = 0; i < nodes.length; i++) {
      var part;
      try {
        part = JSON.parse(nodes[i].textContent);
        // A text-typed metafield arrives double-encoded (a JSON string holding
        // JSON). Unwrap it rather than dropping the whole index.
        if (typeof part === 'string') part = JSON.parse(part);
      } catch (e) {
        continue;
      }
      var map = part && part.m ? part.m : part;
      if (!map || typeof map !== 'object') continue;
      if (!merged) merged = {};
      for (var k in map) {
        if (Object.prototype.hasOwnProperty.call(map, k)) merged[k] = map[k];
      }
    }
    return merged;
  }

  /**
   * 48-bit FNV-1a of the AWB, as 12 hex chars. Kept SYNCHRONOUS on purpose:
   * the lookup runs inside the form-submit handler, and an `await` here would
   * spend the user gesture, so the popup blocker would kill the new tab that
   * every non-index path still needs to open.
   *
   * What this buys, honestly: nobody can view-source the page and walk away
   * with a list of live AWB numbers to paste into the courier trackers. It is
   * NOT proof against brute force — an AWB series is low-entropy enough to
   * enumerate offline, and SHA-256 would not change that. If that matters more
   * than the on-page timeline, turn the index off in the section settings and
   * the page falls back to pattern routing.
   *
   * scripts/track-sync.mjs implements this byte-for-byte. Change one, change both.
   */
  function fnv1a(str, seed) {
    var h = seed >>> 0;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h >>> 0;
  }

  function hashAwb(awb) {
    var s = String(awb == null ? '' : awb).toUpperCase();
    var a = fnv1a(s, 0x811c9dc5);
    var b = fnv1a(s + '#rynoka', 0x01000193);
    return (
      ('00000000' + a.toString(16)).slice(-8) + ('0000' + (b & 0xffff).toString(16)).slice(-4)
    );
  }

  /** "sf|od|2026-08-27" -> { carrier:'sf', status:'out_for_delivery', date:… } */
  var STATUS_CODE = {
    pl: 'placed',
    cf: 'confirmed',
    pk: 'packed',
    sh: 'shipped',
    od: 'out_for_delivery',
    dl: 'delivered',
    ex: 'exception',
    cx: 'cancelled'
  };

  function parseIndexEntry(raw) {
    if (!raw) return null;
    var bits = String(raw).split('|');
    if (!bits.length) return null;
    return {
      carrier: bits[0] || '',
      status: STATUS_CODE[bits[1]] || bits[1] || '',
      date: bits[2] || ''
    };
  }

  /** Timeline steps come from the section's `status_label` blocks. */
  function steps(section) {
    var list = readJson(section, '[data-tl-track-steps]');
    if (Array.isArray(list) && list.length) return list;
    return [
      { key: 'placed', label: 'Order Placed' },
      { key: 'confirmed', label: 'Confirmed' },
      { key: 'shipped', label: 'Shipped' },
      { key: 'out_for_delivery', label: 'Out for Delivery' },
      { key: 'delivered', label: 'Delivered' }
    ];
  }

  var STATUS_LABEL = {
    placed: 'Order Placed',
    confirmed: 'Confirmed',
    packed: 'Packed',
    shipped: 'Shipped',
    out_for_delivery: 'Out for Delivery',
    delivered: 'Delivered',
    exception: 'Delivery Issue',
    cancelled: 'Cancelled'
  };

  function shiprocketUrl(cfg, awb) {
    var path = cfg.srPath.charAt(0) === '/' ? cfg.srPath : '/' + cfg.srPath;
    path = path.replace(/\/+$/, '');
    // path style  -> https://shiprocket.co/tracking/AWB123
    if (cfg.srStyle === 'path') {
      return cfg.srBase + path + '/' + encodeURIComponent(awb);
    }
    // query style -> https://yourbrand.shiprocket.co/tracking?awb=AWB123
    return (
      cfg.srBase + path + '?' + encodeURIComponent(cfg.srParam) + '=' + encodeURIComponent(awb)
    );
  }

  /* --------------------------------------------------------------- errors */

  function showError(section, msg) {
    var el = section.querySelector('[data-tl-track-error]');
    if (!el) return;
    el.textContent = msg;
    el.removeAttribute('hidden');
  }

  function clearError(section) {
    var el = section.querySelector('[data-tl-track-error]');
    if (!el) return;
    el.setAttribute('hidden', '');
    el.textContent = '';
  }

  function clearResult(section) {
    var wrap = section.querySelector('[data-tl-track-result]');
    var root = section.querySelector('[data-tl-track-root]');
    if (root) root.innerHTML = '';
    if (wrap) wrap.setAttribute('hidden', '');
  }

  /* ------------------------------------------------------------- markup */

  var ICON_CHECK =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>';

  function timelineHtml(section, cfg, order) {
    var list = steps(section);
    var currentIdx = -1;
    for (var i = 0; i < list.length; i++) {
      if (list[i].key === order.status) {
        currentIdx = i;
        break;
      }
    }

    // Per-step timestamps, when the API supplies them.
    var times = {};
    if (Array.isArray(order.steps)) {
      order.steps.forEach(function (st) {
        if (st && st.key) times[st.key] = st.time;
      });
    }

    var html = '';
    list.forEach(function (step, i) {
      var done = currentIdx > -1 && i < currentIdx;
      var current = i === currentIdx;
      var state = done ? 'done' : current ? 'current' : 'pending';
      var time =
        times[step.key] || step.note || (done ? 'Completed' : current ? 'In progress' : 'Pending');

      html += '<div class="tl-track__step tl-track__step--' + state + '">';
      html += '<div class="tl-track__step-rail">';
      html += '<span class="tl-track__step-dot">';
      if (done) html += ICON_CHECK;
      else if (current) html += '<span class="tl-track__step-pulse"></span>';
      html += '</span>';
      if (i < list.length - 1) html += '<span class="tl-track__step-line"></span>';
      html += '</div>';
      html += '<div class="tl-track__step-body">';
      html += '<p class="tl-track__step-label">' + esc(step.label) + '</p>';
      html += '<p class="tl-track__step-time">' + esc(time) + '</p>';
      html += '</div>';
      html += '</div>';
    });
    return html;
  }

  function itemsHtml(cfg, order) {
    if (!Array.isArray(order.items) || !order.items.length) return '';

    var html = '';
    html += '<div class="tl-track__card tl-track__card--aside">';
    html += '<h3 class="tl-track__card-title">' + esc(cfg.itemsHeading) + '</h3>';

    order.items.forEach(function (it) {
      html += '<div class="tl-track__item">';
      html += '<div class="tl-track__item-thumb">';
      if (it.image) {
        html += '<img src="' + esc(it.image) + '" alt="" loading="lazy" width="52" height="52">';
      }
      html += '</div>';
      html += '<div class="tl-track__item-body">';
      html += '<p class="tl-track__item-title">' + esc(it.title) + '</p>';
      html += '<p class="tl-track__item-meta">Qty ' + esc(it.qty) + ' · ' + esc(fmtINR(it.price)) + '</p>';
      html += '</div>';
      html += '</div>';
    });

    if (order.total != null && order.total !== '') {
      html += '<div class="tl-track__total">';
      html += '<span class="tl-track__total-label">' + esc(cfg.totalLabel) + '</span>';
      html += '<span class="tl-track__total-value">' + esc(fmtINR(order.total)) + '</span>';
      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  /** Full order panel — timeline + summary aside, per the design mock. */
  function orderHtml(section, cfg, order) {
    var statusKey = String(order.status || '').toLowerCase();
    var statusLabel = STATUS_LABEL[statusKey] || statusKey.replace(/_/g, ' ');
    var meta = [];
    if (order.date) meta.push('Placed on ' + order.date);
    if (order.eta) meta.push(order.eta);

    var html = '';
    html += '<div class="tl-track__result-grid tl-track__reveal" data-tl-track-card>';

    // Timeline
    html += '<div class="tl-track__card">';
    html += '<div class="tl-track__order-head">';
    html += '<h2 class="tl-track__order-id">Order ' + esc(order.id) + '</h2>';
    html +=
      '<span class="tl-track__status tl-track__status--' +
      esc(statusKey) +
      '">' +
      esc(statusLabel) +
      '</span>';
    html += '</div>';
    if (meta.length) {
      html += '<p class="tl-track__order-meta">' + esc(meta.join(' · ')) + '</p>';
    }
    html += '<div class="tl-track__timeline">' + timelineHtml(section, cfg, order) + '</div>';
    html += '</div>';

    // Aside
    html += '<div>';
    html += itemsHtml(cfg, order);
    if (order.address) {
      html += '<div class="tl-track__card tl-track__card--aside">';
      html += '<h3 class="tl-track__card-title">' + esc(cfg.addressHeading) + '</h3>';
      html += '<p class="tl-track__address">' + esc(order.address) + '</p>';
      html += '</div>';
    }
    html +=
      '<a class="tl-track__aside-cta" href="' +
      esc(cfg.contactUrl) +
      '">' +
      esc(cfg.helpCta) +
      '</a>';
    html += '</div>';

    html += '</div>';
    return html;
  }

  /** Shiprocket hand-off card — all we can show without an order payload. */
  function handoffHtml(cfg, awb, opened) {
    var url = shiprocketUrl(cfg, awb);
    // The legacy path has exactly one courier, so resolve [carrier] to it
    // rather than printing the raw token at the shopper.
    var heading =
      opened && cfg.srEmbed !== 'card_only'
        ? withCarrierName(cfg.handoffTitle, 'Shiprocket')
        : cfg.handoffBlocked;

    var html = '';
    html += '<div class="tl-track__handoff tl-track__reveal" data-tl-track-card role="status">';
    html += '<span class="tl-track__handoff-icon" aria-hidden="true">';
    html +=
      '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    html += '</span>';
    html += '<p class="tl-track__handoff-title">' + esc(heading) + '</p>';
    html += '<p class="tl-track__handoff-key">AWB</p>';
    html +=
      '<p class="tl-track__handoff-awb"><button type="button" class="tl-track__sample-link" style="font-family:inherit;font-size:inherit;font-weight:800;color:inherit" data-tl-copy="' +
      esc(awb) +
      '" title="Copy AWB">' +
      esc(awb) +
      '</button></p>';
    html +=
      '<a class="tl-track__handoff-cta" href="' +
      esc(url) +
      '" target="_blank" rel="noopener noreferrer">' +
      esc(cfg.handoffCta) +
      '</a>';
    if (cfg.handoffNote) {
      html += '<p class="tl-track__handoff-note">' + esc(cfg.handoffNote) + '</p>';
    }
    html += '</div>';
    return html;
  }

  /* ------------------------------------------------- multi-carrier markup */

  function carrierChip(carrier) {
    if (!carrier) return '';
    var accent = carrier.accent || 'var(--tl-accent, #ff6b35)';
    return (
      '<span class="tl-track__carrier-chip" style="--tl-carrier-accent:' +
      esc(accent) +
      '">' +
      esc(carrier.name || carrier.code) +
      '</span>'
    );
  }

  /** Carrier-aware hand-off. Same shape as handoffHtml, but names the courier. */
  function carrierHandoffHtml(cfg, carrier, awb, opened) {
    var url = fillTemplate(carrier.url_template, awb);
    var name = carrier.name || carrier.code;
    /* [carrier] is the house token. The trailing Shiprocket swap keeps copy
       written before multi-carrier support from naming the wrong courier —
       a Delhivery parcel must never say "opened on Shiprocket". */
    var heading =
      opened && cfg.srEmbed !== 'card_only'
        ? withCarrierName(cfg.handoffTitle, name).replace(/Shiprocket/gi, name)
        : cfg.handoffBlocked;

    var html = '';
    html += '<div class="tl-track__handoff tl-track__reveal" data-tl-track-card role="status">';
    html += '<span class="tl-track__handoff-icon" aria-hidden="true">';
    html +=
      '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    html += '</span>';
    html += '<p class="tl-track__handoff-title">' + esc(heading) + '</p>';
    html += '<p class="tl-track__handoff-key">' + esc(cfg.carrierLabel) + '</p>';
    html += '<p class="tl-track__handoff-carrier">' + carrierChip(carrier) + '</p>';
    html += '<p class="tl-track__handoff-key">AWB</p>';
    html +=
      '<p class="tl-track__handoff-awb"><button type="button" class="tl-track__sample-link" style="font-family:inherit;font-size:inherit;font-weight:800;color:inherit" data-tl-copy="' +
      esc(awb) +
      '" title="Copy AWB">' +
      esc(awb) +
      '</button></p>';
    html +=
      '<a class="tl-track__handoff-cta" href="' +
      esc(url) +
      '" target="_blank" rel="noopener noreferrer">' +
      esc(withCarrierName(cfg.liveCta, name)) +
      '</a>';
    if (cfg.handoffNote) {
      html +=
        '<p class="tl-track__handoff-note">' +
        esc(withCarrierName(cfg.handoffNote, name)) +
        '</p>';
    }
    html += '</div>';
    return html;
  }

  /**
   * Ambiguous or unrecognised AWB — offer the couriers instead of an error.
   * A dead end is never acceptable here: the shopper has a real parcel.
   */
  function chooserHtml(cfg, list, awb) {
    var html = '';
    html += '<div class="tl-track__handoff tl-track__reveal" data-tl-track-card role="group">';
    html += '<span class="tl-track__handoff-icon tl-track__handoff-icon--ask" aria-hidden="true">';
    html +=
      '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12" y2="17"/></svg>';
    html += '</span>';
    html += '<p class="tl-track__handoff-title">' + esc(cfg.chooserTitle) + '</p>';
    html += '<p class="tl-track__handoff-key">AWB</p>';
    html += '<p class="tl-track__handoff-awb">' + esc(awb) + '</p>';
    html += '<div class="tl-track__chooser">';
    list.forEach(function (c) {
      if (!c || !c.url_template) return;
      html +=
        '<a class="tl-track__chooser-btn" style="--tl-carrier-accent:' +
        esc(c.accent || 'var(--tl-accent, #ff6b35)') +
        '" href="' +
        esc(fillTemplate(c.url_template, awb)) +
        '" target="_blank" rel="noopener noreferrer">' +
        esc(c.name || c.code) +
        '</a>';
    });
    html += '</div>';
    html += '<p class="tl-track__handoff-note">' + esc(cfg.chooserNote) + '</p>';
    html += '</div>';
    return html;
  }

  /**
   * The Tier-2 payoff: a REAL timeline, built from Shopify's own shipment
   * status. No items or address — those are personal, and this index is public.
   */
  function shipmentHtml(section, cfg, carrier, awb, entry) {
    var statusKey = String(entry.status || '').toLowerCase();
    var statusLabel = STATUS_LABEL[statusKey] || statusKey.replace(/_/g, ' ');
    var url = carrier ? fillTemplate(carrier.url_template, awb) : '';

    var html = '';
    html += '<div class="tl-track__result-grid tl-track__reveal" data-tl-track-card>';

    html += '<div class="tl-track__card">';
    html += '<div class="tl-track__order-head">';
    html += '<h2 class="tl-track__order-id">AWB ' + esc(awb) + '</h2>';
    html +=
      '<span class="tl-track__status tl-track__status--' +
      esc(statusKey) +
      '">' +
      esc(statusLabel) +
      '</span>';
    html += '</div>';
    var meta = [];
    if (carrier) meta.push((carrier.name || carrier.code) + ' ');
    if (entry.date) meta.push(cfg.lastUpdate + ' ' + entry.date);
    if (meta.length) {
      html += '<p class="tl-track__order-meta">' + esc(meta.join(' · ')) + '</p>';
    }
    html +=
      '<div class="tl-track__timeline">' +
      timelineHtml(section, cfg, { status: statusKey }) +
      '</div>';
    html += '</div>';

    html += '<div>';
    html += '<div class="tl-track__card tl-track__card--aside">';
    html += '<h3 class="tl-track__card-title">' + esc(cfg.carrierLabel) + '</h3>';
    html += '<p class="tl-track__handoff-carrier">' + carrierChip(carrier) + '</p>';
    if (url) {
      html +=
        '<a class="tl-track__handoff-cta tl-track__handoff-cta--block" href="' +
        esc(url) +
        '" target="_blank" rel="noopener noreferrer">' +
        esc(withCarrierName(cfg.liveCta, carrier ? carrier.name || carrier.code : 'courier')) +
        '</a>';
    }
    html += '</div>';
    html +=
      '<a class="tl-track__aside-cta" href="' +
      esc(cfg.contactUrl) +
      '">' +
      esc(cfg.helpCta) +
      '</a>';
    html += '</div>';

    html += '</div>';
    return html;
  }

  function paint(section, html) {
    var wrap = section.querySelector('[data-tl-track-result]');
    var root = section.querySelector('[data-tl-track-root]');
    if (!wrap || !root) return;

    root.innerHTML = html;
    wrap.removeAttribute('hidden');

    requestAnimationFrame(function () {
      var card = root.querySelector('[data-tl-track-card]');
      if (card) card.classList.add(ENTER);
    });
  }

  /* --------------------------------------------------------------- lookup */

  function setBusy(section, busy) {
    var btn = section.querySelector('[data-tl-track-submit]');
    if (!btn) return;
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    } else {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }

  function openTracking(url) {
    try {
      return !!window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      return false;
    }
  }

  function fromApi(cfg, awb) {
    var sep = cfg.api.indexOf('?') > -1 ? '&' : '?';
    var url = cfg.api + sep + 'awb=' + encodeURIComponent(awb);
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function runTrack(section) {
    var input = section.querySelector('[data-tl-track-input]');
    var cfg = config(section);
    var awb = cleanAwb(input && input.value);

    clearError(section);

    if (!awb) {
      clearResult(section);
      showError(section, cfg.msgEmpty);
      if (input && input.focus) input.focus();
      return;
    }

    // 1. Sample payload (editor preview only).
    var sample = readJson(section, '[data-tl-track-sample]');
    if (sample && sample[awb]) {
      var order = sample[awb];
      order.id = awb;
      paint(section, orderHtml(section, cfg, order));
      return;
    }

    // 2. Custom tracking API.
    if (cfg.provider === 'api' && cfg.api) {
      setBusy(section, true);
      fromApi(cfg, awb)
        .then(function (data) {
          if (!data || data.found === false || (!data.status && !data.items)) {
            clearResult(section);
            showError(section, cfg.msgNotFound);
            return;
          }
          if (!data.id) data.id = awb;
          paint(section, orderHtml(section, cfg, data));
        })
        .catch(function () {
          clearResult(section);
          showError(section, cfg.msgError);
        })
        .then(function () {
          setBusy(section, false);
        });
      return;
    }

    // 3. Multi-carrier routing. With no carrier blocks configured this returns
    //    `legacy` and the original single-Shiprocket behaviour runs untouched.
    var route = routeCarrier(section, cfg, awb);

    if (route.mode === 'legacy') {
      var opened = false;
      if (cfg.srEmbed !== 'card_only') {
        opened = openTracking(shiprocketUrl(cfg, awb));
      }
      paint(section, handoffHtml(cfg, awb, opened));
      return;
    }

    if (route.mode === 'chooser') {
      // Never auto-open on an ambiguous number — the wrong tracker is worse
      // than a question. The shopper picks, then we open.
      paint(section, chooserHtml(cfg, route.list, awb));
      return;
    }

    if (route.mode === 'shipment') {
      // We know the real status, so the timeline IS the answer. Opening a tab
      // on top of it would bury the thing the shopper came for.
      paint(section, shipmentHtml(section, cfg, route.carrier, awb, route.entry));
      return;
    }

    var url = fillTemplate(route.carrier.url_template, awb);
    var didOpen = false;
    if (cfg.srEmbed !== 'card_only' && url) {
      didOpen = openTracking(url);
    }
    paint(section, carrierHandoffHtml(cfg, route.carrier, awb, didOpen));
  }

  /* --------------------------------------------------------- the resolver */

  /** Carrier forced by ?carrier= — the shipping-confirmation email deep link. */
  function urlCarrierCode() {
    try {
      return new URLSearchParams(window.location.search).get('carrier') || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Know where we can, guess where we must, ask when we can't.
   *
   *   1. ?carrier= deep link      — the email already knew; trust it
   *   2. AWB index                — mirrored from Shopify's own fulfillments
   *   3. exactly one pattern hit  — unambiguous, use it
   *   4. 2+ pattern hits          — ask, never guess between real candidates
   *   5. fallback carrier         — Shiprocket resolves its own network
   *   6. nothing configured       — ask
   */
  function routeCarrier(section, cfg, awb) {
    var list = carriers(section);
    if (!list.length) return { mode: 'legacy' };

    var forced = carrierByCode(list, urlCarrierCode());
    if (forced) return { mode: 'carrier', carrier: forced, source: 'link' };

    var index = awbIndex(section);
    if (index) {
      var entry = parseIndexEntry(index[hashAwb(awb)]);
      if (entry) {
        var known = carrierByCode(list, entry.carrier) || fallbackCarrier(list);
        if (entry.status) {
          return { mode: 'shipment', carrier: known, entry: entry, source: 'index' };
        }
        if (known) return { mode: 'carrier', carrier: known, source: 'index' };
      }
    }

    var hits = matchByPattern(list, awb);
    if (hits.length === 1) return { mode: 'carrier', carrier: hits[0], source: 'pattern' };
    if (hits.length > 1) return { mode: 'chooser', list: hits, source: 'ambiguous' };

    var fb = fallbackCarrier(list);
    if (fb) return { mode: 'carrier', carrier: fb, source: 'fallback' };

    return { mode: 'chooser', list: list, source: 'unknown' };
  }

  // Exposed for tests/track-carrier.test.mjs — no storefront code reads this.
  window.__tlTrackOrder = {
    routeCarrier: routeCarrier,
    hashAwb: hashAwb,
    fillTemplate: fillTemplate,
    matchByPattern: matchByPattern,
    parseIndexEntry: parseIndexEntry,
    config: config
  };

  /* ------------------------------------------------------------ listeners */

  function sectionOf(el) {
    return el && el.closest ? el.closest('[data-tl-track-order]') : null;
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-tl-track-form]')) return;
    e.preventDefault();
    var section = sectionOf(form);
    if (section) runTrack(section);
  });

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // Sample AWB link
    var fill = t.closest('[data-tl-track-sample-fill]');
    if (fill) {
      var sec = sectionOf(fill);
      var inp = sec && sec.querySelector('[data-tl-track-input]');
      if (sec && inp) {
        inp.value = fill.getAttribute('data-tl-track-sample-fill') || '';
        runTrack(sec);
      }
      return;
    }

    // FAQ accordion
    var faq = t.closest('[data-tl-faq-toggle]');
    if (faq) {
      var open = faq.getAttribute('aria-expanded') === 'true';
      var panel = document.getElementById(faq.getAttribute('aria-controls'));
      faq.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (panel) {
        if (open) panel.setAttribute('hidden', '');
        else panel.removeAttribute('hidden');
      }
      return;
    }

    // Copy-to-clipboard (AWB)
    var copy = t.closest('[data-tl-copy]');
    if (copy) {
      var section = sectionOf(copy);
      var val = copy.getAttribute('data-tl-copy');
      if (!section || !val || !navigator.clipboard || !navigator.clipboard.writeText) return;
      navigator.clipboard.writeText(val).then(function () {
        var toast = section.querySelector('[data-tl-track-toast]');
        var txt = section.querySelector('[data-tl-track-toast-txt]');
        if (!toast) return;
        if (txt) txt.textContent = config(section).copied;
        toast.classList.add(ENTER);
        setTimeout(function () {
          toast.classList.remove(ENTER);
        }, 2000);
      });
    }
  });

  // Deep link: /pages/track-order?awb=XXXX auto-runs the lookup.
  function initDeepLink() {
    var section = document.querySelector('[data-tl-track-order]');
    if (!section) return;
    var awb = new URLSearchParams(window.location.search).get('awb');
    if (!awb) return;
    var input = section.querySelector('[data-tl-track-input]');
    if (!input) return;
    input.value = awb;
    runTrack(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeepLink);
  } else {
    initDeepLink();
  }
})();
